import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  DEFAULT_SEGMENT,
  type GlobalSchemeDefault,
  type PremiseConfig,
  type SchemeKey,
} from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import { defaultSchemeBinding } from "@shared/schemes/content";
import { foyerApi } from "../../http/foyer";
import SchemeThumbnail from "./SchemeThumbnail";

interface Props {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}

/**
 * Choose which bespoke template a premise renders. Picking one seeds a default
 * binding (demo content keyed off the premise's branding). Switching templates
 * keeps the operator's content - only the per-scheme token/knob overrides reset,
 * since those reference scheme-specific keys.
 */
export default function SchemePicker({ premise, onChange }: Props) {
  const { t } = useTranslation(["templates", "common"]);
  const current = premise.scheme?.key;
  const segment = premise.segment ?? DEFAULT_SEGMENT;
  // Only offer templates that serve this premise's business type.
  const options = Object.values(SCHEME_MANIFESTS).filter(
    (m) => m.segment === segment
  );

  // Global per-template defaults (admin-set colours + name), seeded on pick.
  const [defaults, setDefaults] = useState<Record<string, GlobalSchemeDefault>>(
    {}
  );
  useEffect(() => {
    let alive = true;
    foyerApi
      .listSchemeDefaults()
      .then((list) => {
        if (!alive) return;
        const map: Record<string, GlobalSchemeDefault> = {};
        for (const d of list) map[d.key] = d;
        setDefaults(map);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  function pick(key: SchemeKey) {
    if (key === current) return;
    const existing = premise.scheme;
    // A switch is saved the moment it is picked, and it resets the colours,
    // fonts and screen settings (they refer to the old template's keys). Say so
    // first. Content (names, Wi-Fi, sections...) is kept.
    if (
      existing &&
      !window.confirm(
        t("schemePicker.switchConfirm", {
          name: defaults[key]?.name ?? SCHEME_MANIFESTS[key]?.name ?? key,
        })
      )
    ) {
      return;
    }
    const base = existing
      ? { key, content: existing.content } // keep content, drop tokens/knobs
      : defaultSchemeBinding(key, {
          hotelName: premise.premiseName,
          city: premise.address?.city,
        });
    // Seed the admin's global default colours (new application only).
    const palette = defaults[key]?.palette;
    const scheme =
      palette && Object.keys(palette).length
        ? {
            ...base,
            tokens: {
              ...base.tokens,
              palette: { ...base.tokens?.palette, ...palette },
            },
          }
        : base;
    onChange({ ...premise, scheme });
  }

  return (
    <Stack spacing={2} data-testid="scheme-picker">
      <Typography variant="body2" color="text.secondary">
        <Trans
          t={t}
          i18nKey={
            segment === "care"
              ? "schemePicker.showingCare"
              : "schemePicker.showingHotel"
          }
          components={{ strong: <strong /> }}
        />
      </Typography>
      {options.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t("schemePicker.noTemplates")}
        </Typography>
      )}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          // Wrap onto multiple rows so each card is wide enough to read the
          // description AND show every swatch (incl. the accent that sets the
          // templates apart). ~3-4 per row on a normal screen.
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
            lg: "repeat(4, 1fr)",
          },
        }}
      >
        {options.map((m) => {
          const selected = m.key === current;
          return (
            <Card
              key={m.key}
              variant="outlined"
              sx={{
                borderColor: selected ? "primary.main" : "divider",
                borderWidth: selected ? 2 : 1,
                position: "relative",
              }}
            >
              <CardActionArea
                onClick={() => pick(m.key)}
                data-testid={`scheme-option-${m.key}`}
                sx={{
                  p: 2,
                  height: "100%",
                  // Top-align content so the swatch row + name sit at the same
                  // height on every card regardless of description length
                  // (otherwise short-description cards center-float downward).
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                }}
              >
                {selected && (
                  <CheckCircleIcon
                    color="primary"
                    fontSize="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      borderRadius: "50%",
                    }}
                  />
                )}
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <SchemeThumbnail schemeKey={m.key} premise={premise} />
                  <Typography variant="subtitle1">
                    {defaults[m.key]?.name ?? m.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {m.description}
                  </Typography>
                  {selected && (
                    <Chip
                      label={t("schemePicker.selected")}
                      size="small"
                      color="primary"
                    />
                  )}
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Stack>
  );
}
